import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { HID_EMAIL_MESSAGE, isHidGlobalEmail } from "../auth/hidEmail";
import {
  createLoginFlow,
  LoginFlow,
  requestSignInCode,
  resendSignInCode,
  signInWithCode,
  signInWithPassword,
  signInWithFIDO,
  SubmitResult,
} from "../auth/kratosLogin";

type Step = "credentials" | "code" | "fido";

const FLOW_UNAVAILABLE =
  "Cannot reach the identity service. Confirm the Ory stack is running on port 4433.";

interface LoginFormProps {
  onForgotPassword?: () => void;
}

export function LoginForm({ onForgotPassword }: LoginFormProps) {
  const { refresh } = useAuth();

  const [flow, setFlow] = useState<LoginFlow | null>(null);
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"password" | "code" | "resend" | "fido" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [fidoAvailable, setFidoAvailable] = useState(false);

  const startFlow = useCallback(async () => {
    try {
      setFlow(await createLoginFlow());
    } catch {
      setError(FLOW_UNAVAILABLE);
    }
  }, []);

  useEffect(() => {
    void startFlow();
  }, [startFlow]);

  useEffect(() => {
    const hasWebAuthn = !!navigator.credentials?.get;
    setFidoAvailable(hasWebAuthn);
  }, []);

  /** Applies whatever Kratos said, and returns true when we are signed in. */
  const apply = useCallback(
    async (result: SubmitResult): Promise<boolean> => {
      switch (result.kind) {
        case "success":
        case "alreadySignedIn":
          await refresh();
          return true;
        case "codeSent":
          setFlow(result.flow);
          setStep("code");
          setCode("");
          setError(null);
          setInfo(result.message);
          return false;
        case "expired":
          setStep("credentials");
          setCode("");
          setInfo(null);
          setError("That sign-in attempt timed out. Please try again.");
          await startFlow();
          return false;
        case "error":
          if (result.flow) setFlow(result.flow);
          setInfo(null);
          setError(result.message);
          return false;
      }
    },
    [refresh, startFlow]
  );

  const run = async (
    kind: "password" | "code" | "resend" | "fido",
    action: (current: LoginFlow) => Promise<SubmitResult>
  ) => {
    if (!flow) {
      setError(FLOW_UNAVAILABLE);
      return;
    }
    setBusy(kind);
    try {
      await apply(await action(flow));
    } catch {
      setError(FLOW_UNAVAILABLE);
    } finally {
      setBusy(null);
    }
  };

  const trimmedEmail = email.trim();
  const emailMissing = trimmedEmail.length === 0;

  const requireHidEmail = (): boolean => {
    if (isHidGlobalEmail(trimmedEmail)) return true;
    setInfo(null);
    setError(HID_EMAIL_MESSAGE);
    return false;
  };

  const handlePasswordSignIn = (event: React.FormEvent) => {
    event.preventDefault();
    if (emailMissing || !password) {
      setError("Enter your email address and password.");
      return;
    }
    if (!requireHidEmail()) return;
    void run("password", (f) => signInWithPassword(f, trimmedEmail, password));
  };

  const handleRequestCode = () => {
    if (emailMissing) {
      setError("Enter your email address first, then request a code.");
      return;
    }
    if (!requireHidEmail()) return;
    void run("code", (f) => requestSignInCode(f, trimmedEmail));
  };

  const handleSubmitCode = (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim()) {
      setError("Enter the code from your email.");
      return;
    }
    void run("code", (f) => signInWithCode(f, trimmedEmail, code.replace(/\s/g, "")));
  };

  const handleFIDOSignIn = () => {
    if (!fidoAvailable) {
      setError("WebAuthn not available in your browser");
      return;
    }
    if (emailMissing) {
      setError("Enter your email address first, then use your security key.");
      return;
    }
    if (!requireHidEmail()) return;
    void run("fido", (f) => signInWithFIDO(f, trimmedEmail));
  };

  const backToCredentials = () => {
    setStep("credentials");
    setCode("");
    setError(null);
    setInfo(null);
  };

  const fieldClass =
    "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20";

  return (
    <div className="mt-6">
      {error && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        >
          {error}
        </div>
      )}
      {info && !error && (
        <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
          {info}
        </div>
      )}

      {step === "credentials" ? (
        <form onSubmit={handlePasswordSignIn} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Email address
            </span>
            <input
              type="email"
              value={email}
              autoComplete="username"
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@hidglobal.com"
              className={fieldClass}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Password
            </span>
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className={fieldClass}
            />
          </label>

          <button
            type="submit"
            disabled={busy !== null || !flow}
            className="rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {busy === "password" ? "Signing in…" : "Sign in with password"}
          </button>

          {onForgotPassword && (
            <button
              type="button"
              onClick={onForgotPassword}
              disabled={busy !== null}
              className="-mt-2 self-end text-xs font-semibold text-brand-navy hover:underline disabled:opacity-50"
            >
              Forgot password?
            </button>
          )}

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              or
            </span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={handleRequestCode}
            disabled={busy !== null || !flow}
            className="rounded-xl border border-brand-accent/40 bg-brand-accent/5 px-4 py-3 text-sm font-semibold text-brand-navy transition hover:bg-brand-accent/10 disabled:opacity-50"
          >
            {busy === "code" ? "Sending code…" : "Send sign in code to my email"}
          </button>
          <p className="text-xs text-slate-500">
            No password needed. We email a 6-digit code that is valid for 15 minutes.
          </p>

          {fidoAvailable && (
            <>
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-slate-200" />
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  or
                </span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>

              <button
                type="button"
                onClick={handleFIDOSignIn}
                disabled={busy !== null || !flow}
                className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-900 transition hover:bg-blue-100 disabled:opacity-50"
              >
                {busy === "fido" ? "Verifying with security key…" : "Sign in with security key"}
              </button>
              <p className="text-xs text-slate-500">
                Use your FIDO2 security key, biometric, or device PIN for instant sign-in.
              </p>
            </>
          )}
        </form>
      ) : (
        <form onSubmit={handleSubmitCode} className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            Enter the code sent to <span className="font-semibold">{trimmedEmail}</span>.
          </p>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sign in code
            </span>
            <input
              type="text"
              value={code}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className={`${fieldClass} tracking-[0.4em]`}
            />
          </label>

          <button
            type="submit"
            disabled={busy !== null}
            className="rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {busy === "code" ? "Verifying…" : "Verify and sign in"}
          </button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={backToCredentials}
              disabled={busy !== null}
              className="font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Use a different email
            </button>
            <button
              type="button"
              onClick={() =>
                void run("resend", (f) => resendSignInCode(f, trimmedEmail))
              }
              disabled={busy !== null}
              className="font-semibold text-brand-navy hover:underline disabled:opacity-50"
            >
              {busy === "resend" ? "Resending…" : "Resend code"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
